import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/layout/AppChrome";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import {
  ArrowUpRight, Check, Sparkles, PenTool, BarChart3, Users, FileText, Calendar,
  Rocket, Zap, TrendingUp, ChevronDown, Twitter, Instagram, Linkedin, Youtube,
  Star, Play, ArrowRight, Wand2, Loader2, Link2, Film, Hourglass, MapPin,
} from "lucide-react";

/** Icon registry — capability cards look up their lucide icon by name
 * from the COPY data. Keeping it string-based lets us author the copy
 * without touching JSX imports. */
const ICONS = { Link2, FileText, BarChart3, Sparkles, Film, MapPin };

/* -----------------------------  COPY  ----------------------------- */
const COPY = {
  ar: {
    nav: { features: "المميزات", how: "كيف تعمل", pricing: "الأسعار", faq: "الأسئلة", signIn: "تسجيل الدخول", getStarted: "ابدأ مجاناً" },
    hero: {
      badge: "منصة إدارة التسويق الأولى في السعودية والخليج",
      headline1: "نمّ أعمالك بذكاء...",
      headline2: "وأدر تسويقك من مكان واحد.",
      sub: "DAAM هي منصة ذكاء اصطناعي تساعد الشركات على إدارة المحتوى، التسويق، التحليلات، وخطط النمو من لوحة تحكم واحدة.",
      ctaPrimary: "ابدأ مجاناً",
      ctaSecondary: "شاهد المنصة",
      metrics: [
        { k: "+٣٢٠٪", v: "متوسط نمو المتابعين" },
        { k: "٢٤/٧", v: "مستشار ذكي متاح" },
        { k: "٩٢٪", v: "توفير في وقت الفريق" },
      ],
    },
    trusted: { title: "يثق بنا فرق طموحة عبر المنطقة" },
    features: {
      title: "كل ما يحتاجه فريق التسويق الحديث",
      subtitle: "من التخطيط إلى التنفيذ إلى التقارير — دون التنقّل بين ١٢ أداة.",
      items: [
        { icon: PenTool, title: "استوديو المحتوى", desc: "أنشئ، اعتمد، وأطلق المنشورات عبر جميع القنوات بواجهة موحّدة، مع مولّد نصوص ذكي." },
        { icon: Sparkles, title: "خطط تسويق بالذكاء الاصطناعي", desc: "خطة نمو أسبوعية مخصّصة، توصيات محتوى، وأولويات مدعومة بـ 🤖 مساعد دعم الذكي." },
        { icon: BarChart3, title: "لوحة تحليلات فاخرة", desc: "متابعة المتابعين، التفاعل، الوصول، والعملاء المحتملين مع رؤى الجمهور وأفضل أوقات النشر." },
        { icon: Users, title: "إدارة الفريق", desc: "أدوار وصلاحيات دقيقة — عملاء، مختصو حساب، كتّاب، مصمّمون، ومحرّرون في مساحة واحدة." },
        { icon: FileText, title: "تقارير جاهزة للعرض", desc: "تقارير أسبوعية وشهرية بتصدير PDF بنقرة واحدة، تحمل هويّتك التجارية." },
        { icon: Calendar, title: "الجدولة والتقويم", desc: "تقويم محتوى شهري تفاعلي، جدولة عبر منصّات متعدّدة، وموافقات سلسلة." },
      ],
    },
    how: {
      title: "ثلاث خطوات بسيطة للانطلاق",
      subtitle: "من التسجيل إلى أوّل منشور مجدول في أقل من ٦٠ ثانية.",
      steps: [
        { num: "٠١", icon: Rocket, title: "سجّل واربط قنواتك", desc: "أنشئ مساحة عملك، أضف حسابات إنستغرام، تيك توك، لينكد إن، تويتر، ويوتيوب في ثوانٍ." },
        { num: "٠٢", icon: Zap, title: "دع الذكاء الاصطناعي يخطّط", desc: "أخبرنا عن أهدافك، وسيبني مستشار DAAM خطّتك الأسبوعية مع أفكار محتوى وهاشتاغات جاهزة." },
        { num: "٠٣", icon: TrendingUp, title: "أطلق وقس النمو", desc: "جدول منشوراتك، تابع الأداء لحظياً، وحمّل تقاريرك الاحترافية بنقرة." },
      ],
    },
    pricing: {
      title: "خطط أسعار شفّافة تنمو معك",
      subtitle: "بدون رسوم مخفيّة. ألغِ في أي وقت. جميع الأسعار بالريال السعودي.",
      cycleMonthly: "شهري",
      cycleYear: "سنوي",
      yearlySavingsBadge: "شهران مجاناً",
      trialBadge: "تجربة مجّانية ٧ أيام",
      popular: "الأكثر شيوعاً",
      cta: "ابدأ تجربتك المجّانية",
      ctaEnt: "تواصل مع المبيعات",
      perMonth: "/ شهر",
      perYear: "/ سنة",
      loading: "جارٍ تحميل الخطط...",
    },
    capabilityCard: {
      header: "ماذا يفعل DAAM اليوم؟",
      availableHeader: "متاح الآن",
      inDevHeader: "قيد التطوير",
      available: [
        {
          icon: "Link2",
          title: "ربط Instagram و Snapchat و TikTok",
          desc: "ربط حسابات Instagram و Snapchat و TikTok — جميعها متصلة ومُصادَق عليها عبر الـ API الرسمي للمنصات.",
        },
        {
          icon: "FileText",
          title: "تجهيز المحتوى",
          desc: "استوديو كامل لكتابة المنشورات، توليد الصور، وجدولتها في تقويم موحّد.",
        },
        {
          icon: "BarChart3",
          title: "قياس أداء النشر",
          desc: "تقارير تلقائية لأداء كل منشور — وصول، تفاعل، نقرات، وتحوّلات — مع تصدير PDF.",
        },
      ],
      inDev: [
        {
          icon: "Film",
          title: "إنشاء الفيديو وتحريره",
          desc: "محرّك فيديو كامل الطول قيد الربط. الاستوديو الحالي يدعم النص والصور والجدولة فقط.",
        },
        {
          icon: "MapPin",
          title: "تكامل Google Maps",
          desc: "تكامل Google Maps للتسويق المحلي قيد التطوير حالياً.",
        },
        {
          icon: "Sparkles",
          title: "النشر التلقائي على Snapchat",
          desc: "ربط حساب Snapchat يعمل الآن. النشر التلقائي من داخل DAAM بانتظار الموافقة من Snap Business.",
        },
      ],
    },
    testimonials: {
      title: "ماذا يقول عملاؤنا",
      subtitle: "فرق حقيقية تنمو مع DAAM كل يوم.",
      items: [
        {
          quote: "خلال ٣ أشهر، ضاعفنا متابعينا على إنستغرام تقريباً بفضل خطط النمو الأسبوعية من DAAM. المستشار الذكي يفهم السوق السعودي بشكل مذهل.",
          name: "ريم الشهراني", role: "مديرة التسويق", company: "متجر كنوز",
        },
        {
          quote: "كوكالة تدير ١٥ عميلاً، DAAM اختصرت علينا ما يعادل موظفَين. التقارير التلقائيّة وحدها توفّر ٢٠ ساعة أسبوعياً.",
          name: "خالد المطيري", role: "مؤسّس ومدير تنفيذي", company: "وكالة نبض",
        },
        {
          quote: "أخيراً منصّة عربيّة أوّلاً بمستوى الأدوات العالمية. الفريق يستخدمها يومياً، والعملاء يحبّون التقارير التي تحمل هويّتنا.",
          name: "لينا العتيبي", role: "رئيسة قسم المحتوى", company: "منصّة تجارة",
        },
      ],
    },
    faq: {
      title: "الأسئلة الشائعة",
      subtitle: "كل ما تريد معرفته قبل البدء.",
      items: [
        { q: "هل DAAM متاحة باللغة العربية بالكامل؟", a: "نعم، DAAM عربيّة أوّلاً بدعم RTL كامل. جميع أجزاء المنصّة والذكاء الاصطناعي تتحدّث العربية بطلاقة، والإنجليزية متاحة كذلك بنقرة زر." },
        { q: "ما هي طرق الدفع المدعومة؟", a: "نعمل حالياً على دمج مدى، Apple Pay، وبطاقات فيزا وماستركارد عبر Tap Payments و Stripe. حتى ذلك الحين، اشتراكك يُفعَّل مجّاناً خلال فترة التجربة الأولى." },
        { q: "أيّ منصّات اجتماعية تدعمونها؟", a: "إنستغرام، تيك توك، تويتر (X)، لينكد إن، يوتيوب، فيسبوك، وسناب شات. الربط عبر OAuth مباشر ولا يحتاج نسخ توكن." },
        { q: "هل يمكنني إلغاء الاشتراك في أي وقت؟", a: "بالتأكيد. لا يوجد التزام. يمكنك الإلغاء من صفحة الفوترة، وسيظل حسابك نشطاً حتى نهاية الدورة الحالية." },
        { q: "كيف يعمل مستشار DAAM الذكي؟", a: "يعتمد على نموذج 🤖 مساعد دعم الذكي المُدرَّب خصّيصاً على أفضل ممارسات التسويق في السعودية والخليج. يحلّل نشاطك ويقترح خطّة أسبوعية مخصّصة." },
        { q: "هل بياناتي آمنة؟", a: "نعم. نستخدم تشفير TLS، تخزين مشفّر، مصادقة JWT، وضوابط وصول قائمة على الأدوار. لا نبيع بياناتك أبداً ولا نشاركها مع أطراف ثالثة." },
        { q: "هل تدعمون الوكالات التي تدير عدّة عملاء؟", a: "نعم — خطة Enterprise تُقدَّم مع مساحات فرق متعدّدة، مدير حساب مخصّص، وتقارير موحّدة عبر جميع عملائك." },
      ],
    },
    finalCta: {
      title: "جاهز لتنمو بسرعة الضوء؟",
      subtitle: "انضم إلى الفرق التي تنمو مع DAAM اليوم.",
      cta: "ابدأ مجاناً — بدون بطاقة ائتمان",
      caption: "لا حاجة لبطاقة ائتمان · إعداد في ٦٠ ثانية · إلغاء متى أردت",
    },
    demo: {
      kicker: "جرّب المنصّة الآن",
      title: "احصل على خطّة نموّ ذكيّة في ٣٠ ثانية.",
      subtitle: "أدخل اسم نشاطك وأهدافك، وسيبني مستشار DAAM خطّتك الأسبوعيّة على الفور — بدون تسجيل.",
      businessLabel: "اسم النشاط",
      businessPh: "مثال: مقهى نبض",
      industryLabel: "المجال",
      goalsLabel: "أهدافك (اختر واحد أو أكثر)",
      channelsLabel: "قنواتك النشطة",
      generateBtn: "ولّد خطّتي الآن",
      loading: "جاري بناء خطّتك بالذكاء الاصطناعي...",
      resultTitle: "خطّتك الأسبوعيّة",
      scoreLabel: "مؤشر النمو",
      prioritiesLabel: "أولويّات الأسبوع",
      milestoneLabel: "هدف الأسبوعين القادمين",
      recommendationsLabel: "توصيات",
      remainingLabel: "محاولات متبقّية",
      quotaMsg: "انتهت محاولاتك المجّانية. سجّل الآن لخطّة كاملة ولا محدودة.",
      signupCta: "أعجبتك؟ سجّل مجاناً",
      goals: ["زيادة المتابعين", "زيادة التفاعل", "جذب العملاء", "إطلاق منتج"],
      channels: ["Instagram", "TikTok", "LinkedIn", "YouTube", "X", "Snapchat"],
      industries: ["تجارة إلكترونية", "مطعم أو مقهى", "عيادة أو صحّة", "لياقة", "SaaS", "تعليم", "عقارات", "صانع محتوى", "أخرى"],
    },
    footer: {
      tagline: "منظومة سعودية لإدارة وصناعة المحتوى بالذكاء الاصطناعي",
      product: "المنتج", productLinks: ["استوديو المحتوى", "المساعد الذكي", "التحليلات", "التقارير", "التقويم"],
      company: "الشركة", companyLinks: ["من نحن", "المدوّنة", "الوظائف", "الشراكات"],
      legal: "قانوني", legalLinks: ["شروط الاستخدام", "سياسة الخصوصية", "الأمان", "اتفاقية معالجة البيانات"],
      contact: "تواصل معنا",
      email: "support@getdaam.com",
      phone: "+٩٦٦ ٥٥ ٨٣٧ ١٣٧١",
      phoneHref: "+966558371371",
      rights: "جميع الحقوق محفوظة.",
      madeIn: "صُنع بحب في الرياض",
      sampleBadge: "بيانات توضيحية",
    },
  },
  en: {
    nav: { features: "Features", how: "How it works", pricing: "Pricing", faq: "FAQ", signIn: "Sign in", getStarted: "Start free" },
    hero: {
      badge: "The #1 marketing OS for Saudi Arabia & the GCC",
      headline1: "Grow your business smarter.",
      headline2: "Run marketing from one place.",
      sub: "DAAM is an AI-powered platform that helps businesses manage content, marketing, analytics, and growth plans from a single dashboard.",
      ctaPrimary: "Start free",
      ctaSecondary: "See it in action",
      metrics: [
        { k: "+320%", v: "Avg follower growth" },
        { k: "24/7", v: "AI advisor on call" },
        { k: "92%", v: "Team-hours saved" },
      ],
    },
    trusted: { title: "Trusted by ambitious teams across the region" },
    features: {
      title: "Everything a modern marketing team needs",
      subtitle: "From planning to execution to reporting — without juggling 12 tools.",
      items: [
        { icon: PenTool, title: "Content Studio", desc: "Create, approve, and ship posts across every channel from a single canvas — with an AI copy generator." },
        { icon: Sparkles, title: "AI Marketing Plans", desc: "A personalized weekly growth plan, content recommendations, and priority actions powered by 🤖 مساعد دعم الذكي." },
        { icon: BarChart3, title: "Analytics Dashboard", desc: "Followers, engagement, reach, and leads with audience insights and best-time-to-post recommendations." },
        { icon: Users, title: "Team Management", desc: "Fine-grained roles — clients, account managers, writers, designers, editors — all in one workspace." },
        { icon: FileText, title: "Reports", desc: "Weekly and monthly reports with one-click PDF export, branded with your identity." },
        { icon: Calendar, title: "Scheduling", desc: "Interactive monthly content calendar, multi-platform scheduling, and streamlined approvals." },
      ],
    },
    how: {
      title: "Three simple steps to launch",
      subtitle: "From sign-up to first scheduled post in under 60 seconds.",
      steps: [
        { num: "01", icon: Rocket, title: "Sign up & connect", desc: "Create your workspace and connect Instagram, TikTok, LinkedIn, X, and YouTube in seconds." },
        { num: "02", icon: Zap, title: "Let AI plan", desc: "Tell us your goals — DAAM's advisor builds your weekly plan with ready-to-post ideas and hashtags." },
        { num: "03", icon: TrendingUp, title: "Ship & measure", desc: "Schedule posts, track performance in real time, and export professional reports in one click." },
      ],
    },
    pricing: {
      title: "Transparent pricing that scales with you",
      subtitle: "No hidden fees. Cancel anytime. All prices in Saudi Riyal.",
      cycleMonthly: "Monthly",
      cycleYear: "Yearly",
      yearlySavingsBadge: "2 months free",
      trialBadge: "7-day free trial",
      popular: "Most popular",
      cta: "Start free trial",
      ctaEnt: "Talk to sales",
      perMonth: "/ month",
      perYear: "/ year",
      loading: "Loading plans...",
    },
    capabilityCard: {
      header: "What DAAM does today",
      availableHeader: "Available now",
      inDevHeader: "In development",
      available: [
        {
          icon: "Link2",
          title: "Connect Instagram, Snapchat, and TikTok",
          desc: "Connect your Instagram, Snapchat, and TikTok accounts — all three are already supported and authenticated through each platform's official API.",
        },
        {
          icon: "FileText",
          title: "Prepare your content",
          desc: "A complete studio for writing posts, generating images, and scheduling them in a unified calendar.",
        },
        {
          icon: "BarChart3",
          title: "Measure publishing performance",
          desc: "Automatic per-post reports — reach, engagement, clicks, conversions — with one-click PDF export.",
        },
      ],
      inDev: [
        {
          icon: "Film",
          title: "Video creation and editing",
          desc: "A full-length video engine is being wired in. The current studio supports text, images, and scheduling only.",
        },
        {
          icon: "MapPin",
          title: "Google Maps integration",
          desc: "A Google Maps integration for local-marketing use cases is in development.",
        },
        {
          icon: "Sparkles",
          title: "Automated Snapchat publishing",
          desc: "Snapchat account connection already works. Publishing from inside DAAM is awaiting approval from Snap Business.",
        },
      ],
    },
    testimonials: {
      title: "What our customers say",
      subtitle: "Real teams growing with DAAM every day.",
      items: [
        { quote: "In 3 months we nearly doubled our Instagram followers thanks to DAAM's weekly growth plans. The AI advisor understands the Saudi market remarkably well.", name: "Reem Al-Shahrani", role: "Marketing Manager", company: "Konooz Store" },
        { quote: "As an agency managing 15 clients, DAAM saved us the equivalent of two full-time hires. The automated reports alone save 20 hours a week.", name: "Khalid Al-Mutairi", role: "Founder & CEO", company: "Nabd Agency" },
        { quote: "Finally an Arabic-first platform at the level of global tools. Our team uses it daily, and clients love the branded reports.", name: "Lina Al-Otaibi", role: "Head of Content", company: "Tijarah Platform" },
      ],
    },
    faq: {
      title: "Frequently asked questions",
      subtitle: "Everything you need to know before getting started.",
      items: [
        { q: "Is DAAM fully available in Arabic?", a: "Yes — DAAM is Arabic-first with full RTL support. Every part of the platform, including the AI, speaks fluent Arabic. English is available with one click." },
        { q: "Which payment methods do you support?", a: "We're actively integrating MADA, Apple Pay, and Visa/Mastercard via Tap Payments and Stripe. Until launch, your subscription is activated free during the initial trial." },
        { q: "Which social platforms do you support?", a: "Instagram, TikTok, X (Twitter), LinkedIn, YouTube, Facebook, and Snapchat. Native OAuth — no token copy-paste." },
        { q: "Can I cancel anytime?", a: "Absolutely. No lock-in. Cancel from the billing page and your account stays active until the end of the current cycle." },
        { q: "How does the DAAM AI advisor work?", a: "It's powered by 🤖 مساعد دعم الذكي, tuned specifically on marketing best practices for Saudi Arabia and the GCC. It analyzes your business and proposes a personalized weekly plan." },
        { q: "Is my data secure?", a: "Yes. We use TLS encryption, encrypted storage, JWT authentication, and role-based access control. We never sell your data or share it with third parties." },
        { q: "Do you support agencies with multiple clients?", a: "Yes — the Enterprise plan includes multi-team workspaces, a dedicated account manager, and unified reporting across all your clients." },
      ],
    },
    finalCta: {
      title: "Ready to grow at the speed of light?",
      subtitle: "Join the teams already scaling with DAAM today.",
      cta: "Start free — no credit card",
      caption: "No credit card required · 60-second setup · Cancel anytime",
    },
    demo: {
      kicker: "Try it live",
      title: "Get a smart growth plan in 30 seconds.",
      subtitle: "Enter your business and goals — DAAM's advisor builds your weekly plan instantly. No signup required.",
      businessLabel: "Business name",
      businessPh: "e.g. Nabd Café",
      industryLabel: "Industry",
      goalsLabel: "Your goals (pick one or more)",
      channelsLabel: "Active channels",
      generateBtn: "Generate my plan",
      loading: "Building your plan with AI...",
      resultTitle: "Your weekly plan",
      scoreLabel: "Growth score",
      prioritiesLabel: "This week's priorities",
      milestoneLabel: "14-day milestone",
      recommendationsLabel: "Recommendations",
      remainingLabel: "Free tries left",
      quotaMsg: "You've used your free tries. Sign up for the full unlimited plan.",
      signupCta: "Like what you see? Sign up free",
      goals: ["Grow followers", "Boost engagement", "Generate leads", "Launch a product"],
      channels: ["Instagram", "TikTok", "LinkedIn", "YouTube", "X", "Snapchat"],
      industries: ["E-commerce", "Restaurant / Cafe", "Clinic / Wellness", "Fitness", "SaaS", "Education", "Real estate", "Creator", "Other"],
    },
    footer: {
      tagline: "A Saudi AI-powered content and marketing OS.",
      product: "Product", productLinks: ["Content Studio", "AI Assistant", "Analytics", "Reports", "Calendar"],
      company: "Company", companyLinks: ["About", "Blog", "Careers", "Partners"],
      legal: "Legal", legalLinks: ["Terms of use", "Privacy policy", "Security", "DPA"],
      contact: "Contact",
      email: "support@getdaam.com",
      phone: "+966 55 837 1371",
      phoneHref: "+966558371371",
      rights: "All rights reserved.",
      madeIn: "Made with love in Riyadh",
      sampleBadge: "Sample data",
    },
  },
};

const TRUSTED_BRANDS = {
  ar: ["كنوز", "نبض", "تجارة", "أوركس", "ليمن", "نوفا", "هليوس", "أوريون"],
  en: ["Konooz", "Nabd", "Tijarah", "Orx", "Lymen", "Nova", "Helios", "Orion"],
};

/* ---------------------------  COMPONENT  --------------------------- */
export default function LandingPage() {
  const { lang } = useI18n();
  const t = COPY[lang];
  // Cycle is "monthly" (default) or "yearly" — the legacy "6mo" option
  // was removed Feb 2026 along with the hard-coded USD prices below.
  const [cycle, setCycle] = useState("monthly");
  const [openFaq, setOpenFaq] = useState(0);

  // Plan data fetched from /api/plans — the SINGLE source of truth
  // shared with the signed-in /app/plans page. Future price updates on
  // the backend propagate here automatically.
  const [plansData, setPlansData] = useState(null);
  const [plansError, setPlansError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/plans");
        if (!cancelled) setPlansData(resp.data);
      } catch (e) {
        if (!cancelled) setPlansError(e?.message || "load_failed");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live demo state
  const [demoForm, setDemoForm] = useState({ business_name: "", industry: t.demo.industries[0], goals: [], social_channels: [] });
  const [demoResult, setDemoResult] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState("");

  const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const runDemo = async () => {
    setDemoBusy(true); setDemoError("");
    try {
      const { data } = await api.post("/demo/growth-plan", demoForm);
      setDemoResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || (lang === "ar" ? "تعذّر توليد الخطّة" : "Couldn't generate plan");
      setDemoError(detail);
    } finally {
      setDemoBusy(false);
    }
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-background text-foreground">
      <PublicHeader />

      {/* =========================  HERO  ========================= */}
      <section className="relative overflow-hidden pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="hero-blob" style={{ background: "#0A1F44", width: 520, height: 520, top: -100, insetInlineEnd: -120 }} />
        <div className="hero-blob" style={{ background: "#B8875D", width: 420, height: 420, bottom: -160, insetInlineStart: -100, opacity: 0.25 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground border border-border rounded-full px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B8875D]" />
              {t.hero.badge}
            </div>
            <h1 className="font-display font-medium leading-[1.05] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
              <span className="block text-primary">{t.hero.headline1}</span>
              <span className="block mt-2 shimmer-text">{t.hero.headline2}</span>
            </h1>
            <p className="mt-6 md:mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {t.hero.sub}
            </p>
            <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-primary" className="nx-btn-primary inline-flex items-center gap-2 group">
                {t.hero.ctaPrimary}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <a href="#try-it" data-testid="hero-cta-secondary" className="nx-btn-secondary inline-flex items-center gap-2">
                <Play className="w-3 h-3 fill-current" /> {t.hero.ctaSecondary}
              </a>
            </div>
            <div className="mt-12 md:mt-14 max-w-lg">
              <div className="grid grid-cols-3 gap-6">
                {t.hero.metrics.map((m, i) => (
                  <div key={i} data-testid={`hero-metric-${i}`} className="animate-fade-in-up" style={{ animationDelay: `${200 + i * 100}ms` }}>
                    <div className="font-display text-2xl md:text-3xl text-primary tabular-nums">{m.k}</div>
                    <div className="text-xs text-muted-foreground mt-1">{m.v}</div>
                  </div>
                ))}
              </div>
              {/* Honesty label — these numbers are illustrative targets,
                  not live aggregate production metrics. */}
              <div
                data-testid="hero-sample-badge"
                className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-sm bg-muted text-muted-foreground border border-border"
              >
                {t.footer.sampleBadge}
              </div>
            </div>
          </div>

          {/* Capability showcase — replaces the former illustrative
              dashboard that invented follower counts, engagement rates,
              a "growth score" and a sample AI recommendation. The copy
              is now driven by what DAAM genuinely delivers today, with
              an explicit "in development" section so visitors never
              misjudge the current scope. */}
          <div className="md:col-span-5 relative">
            <div className="relative float-slow">
              <div
                data-testid="capability-card"
                className="nx-card p-6 shadow-[0_30px_80px_-20px_rgba(10,31,68,0.25)]"
              >
                <div className="flex items-center gap-2 pb-4 border-b border-border/60">
                  <span className="w-2 h-2 rounded-full bg-[#B8875D]" />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    {t.capabilityCard.header}
                  </span>
                </div>

                {/* Available now */}
                <div className="mt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[10px] uppercase tracking-widest font-medium text-emerald-700">
                      {t.capabilityCard.availableHeader}
                    </span>
                  </div>
                  <ul className="space-y-3" data-testid="capability-available-list">
                    {t.capabilityCard.available.map((item, i) => {
                      const Icon = ICONS[item.icon] || Check;
                      return (
                        <li key={i} className="flex gap-3">
                          <div className="w-8 h-8 rounded-sm bg-[#B8875D]/10 text-[#B8875D] shrink-0 flex items-center justify-center">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-primary">{item.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {item.desc}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* In development */}
                <div className="mt-6 pt-5 border-t border-border/60">
                  <div className="flex items-center gap-2 mb-3">
                    <Hourglass className="w-3 h-3 text-amber-600" />
                    <span className="text-[10px] uppercase tracking-widest font-medium text-amber-700">
                      {t.capabilityCard.inDevHeader}
                    </span>
                  </div>
                  <ul className="space-y-3" data-testid="capability-indev-list">
                    {t.capabilityCard.inDev.map((item, i) => {
                      const Icon = ICONS[item.icon] || Sparkles;
                      return (
                        <li key={i} className="flex gap-3">
                          <div className="w-8 h-8 rounded-sm bg-amber-500/10 text-amber-700 shrink-0 flex items-center justify-center">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-primary flex items-center gap-2">
                              {item.title}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {item.desc}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================  TRUSTED  ========================= */}
      <section data-testid="trusted-section" className="py-14 border-y border-border/60 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center text-xs uppercase tracking-[0.3em] text-muted-foreground mb-8">{t.trusted.title}</div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 opacity-60">
            {TRUSTED_BRANDS[lang].map((b, i) => (
              <span key={i} className="font-display text-lg md:text-xl tracking-widest text-primary hover:opacity-100 hover:text-[#B8875D] transition-colors cursor-default">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* =========================  FEATURES  ========================= */}
      <section id="features" data-testid="features-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl mb-14 md:mb-20">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{lang === "ar" ? "المميزات" : "Features"}</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight text-primary">{t.features.title}</h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground">{t.features.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60 border border-border/60">
            {t.features.items.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`feature-card-${i}`} className="group bg-background p-8 md:p-10 hover:bg-muted/40 transition-colors relative">
                  <div className="w-12 h-12 rounded-sm bg-primary/5 text-primary flex items-center justify-center mb-6 group-hover:bg-[#B8875D] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-primary">{f.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  <div className="mt-6 flex items-center gap-1 text-xs text-[#B8875D] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>{lang === "ar" ? "اكتشف المزيد" : "Learn more"}</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================  HOW IT WORKS  ========================= */}
      <section id="how" data-testid="how-section" className="py-24 md:py-32 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="hero-blob" style={{ background: "#B8875D", width: 480, height: 480, top: -100, insetInlineStart: -100, opacity: 0.2 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4B08A] mb-3">{lang === "ar" ? "كيف تعمل" : "How it works"}</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight">{t.how.title}</h2>
            <p className="mt-4 text-base md:text-lg text-white/70">{t.how.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {t.how.steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} data-testid={`how-step-${i}`} className="relative">
                  <div className="flex items-start gap-4">
                    <div className="font-display text-5xl md:text-6xl text-[#B8875D]/60 tabular-nums leading-none">{step.num}</div>
                    <div className="flex-1 pt-2">
                      <div className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center mb-4">
                        <Icon className="w-4 h-4 text-[#D4B08A]" />
                      </div>
                      <h3 className="font-display text-xl md:text-2xl">{step.title}</h3>
                      <p className="mt-3 text-sm text-white/70 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================  PRICING  ========================= */}
      <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{lang === "ar" ? "الأسعار" : "Pricing"}</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight text-primary">{t.pricing.title}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t.pricing.subtitle}</p>
            {plansData && (
              <div
                data-testid="pricing-trial-note"
                className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm bg-[#B8875D]/10 text-[#B8875D] border border-[#B8875D]/30"
              >
                <Sparkles className="w-3 h-3" />
                {lang === "ar"
                  ? `تجربة مجّانية ${plansData.trial_days} أيام`
                  : `${plansData.trial_days}-day free trial`}
              </div>
            )}
          </div>

          <div className="flex flex-col items-center mb-10 gap-2">
            <div className="inline-flex bg-muted/50 border border-border rounded-sm p-1">
              <button data-testid="pricing-cycle-monthly" onClick={() => setCycle("monthly")}
                className={`px-6 py-2 text-sm rounded-sm transition ${cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {t.pricing.cycleMonthly}
              </button>
              <button data-testid="pricing-cycle-year" onClick={() => setCycle("yearly")}
                className={`px-6 py-2 text-sm rounded-sm transition ${cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {t.pricing.cycleYear}
              </button>
            </div>
            {plansData && (
              <div
                data-testid="pricing-yearly-savings-note"
                className={`text-xs transition-opacity ${cycle === "yearly" ? "text-emerald-600 opacity-100" : "text-muted-foreground opacity-70"}`}
              >
                {lang === "ar"
                  ? `السنوي يشمل ${plansData.yearly_months_free === 2 ? "شهرين" : plansData.yearly_months_free + " أشهر"} مجاناً`
                  : `Yearly includes ${plansData.yearly_months_free} months free`}
              </div>
            )}
          </div>

          {plansError && (
            <div data-testid="pricing-error" className="text-center text-sm text-destructive mb-6">
              {lang === "ar" ? "تعذّر تحميل الخطط، جرّب تحديث الصفحة." : "Unable to load plans — please refresh."}
            </div>
          )}

          {!plansData && !plansError && (
            <div data-testid="pricing-loading" className="text-center text-sm text-muted-foreground py-10">
              <Loader2 className="w-5 h-5 animate-spin inline-block mx-auto" />
              <div className="mt-2">{t.pricing.loading}</div>
            </div>
          )}

          {plansData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plansData.plans.map((tier) => {
                const amount = tier.prices[cycle];
                const label = lang === "ar"
                  ? (tier.name_ar || tier.name)
                  : tier.name;
                const tagline = lang === "ar"
                  ? (tier.tagline_ar || "")
                  : (tier.tagline_en || "");
                return (
                  <div key={tier.id} data-testid={`plan-card-${tier.id}`}
                    className={`nx-card p-8 md:p-10 flex flex-col relative ${tier.popular ? "border-primary ring-1 ring-primary shadow-[0_20px_60px_-20px_rgba(10,31,68,0.25)]" : ""} hover:-translate-y-1 transition-transform`}>
                    {tier.popular && (
                      <div className="absolute -top-3 start-8 bg-[#B8875D] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-sm flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {t.pricing.popular}
                      </div>
                    )}
                    <div>
                      <h3 data-testid={`plan-name-${tier.id}`} className="font-display text-2xl text-primary">{label}</h3>
                      {tagline && <p className="text-xs text-muted-foreground mt-1">{tagline}</p>}
                    </div>
                    <div className="mt-6 flex items-baseline gap-2">
                      <span data-testid={`plan-price-${tier.id}`} className="font-display text-5xl text-primary tabular-nums">
                        {amount}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {tier.currency || "SAR"} {cycle === "yearly" ? t.pricing.perYear : t.pricing.perMonth}
                      </span>
                    </div>
                    {cycle === "yearly" && (
                      <div className="mt-1 text-[11px] text-emerald-600">
                        {lang === "ar"
                          ? `يشمل ${plansData.yearly_months_free === 2 ? "شهرين" : plansData.yearly_months_free + " أشهر"} مجاناً`
                          : `${plansData.yearly_months_free} months free included`}
                      </div>
                    )}
                    <div className="mt-3 text-[11px] text-muted-foreground">
                      {lang === "ar"
                        ? `بعد تجربة مجّانية ${plansData.trial_days} أيام`
                        : `After a ${plansData.trial_days}-day free trial`}
                    </div>
                    <ul className="mt-6 space-y-3 text-sm flex-1">
                      {(tier.features || []).map((f, i) => (
                        <li key={i} className="flex gap-2">
                          <Check className="w-4 h-4 text-[#B8875D] shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/register?plan=${tier.id}&cycle=${cycle}`}
                      data-testid={`plan-cta-${tier.id}`}
                      className={`mt-8 text-center ${tier.popular ? "nx-btn-accent" : "nx-btn-secondary"}`}
                    >
                      {t.pricing.cta}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* =========================  TESTIMONIALS  ========================= */}
      <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{lang === "ar" ? "شهادات العملاء" : "Testimonials"}</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight text-primary">{t.testimonials.title}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t.testimonials.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.testimonials.items.map((tst, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="nx-card p-8 flex flex-col">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#B8875D] text-[#B8875D]" />)}
                </div>
                <blockquote className="text-base md:text-lg leading-relaxed flex-1 text-primary/90">
                  "{tst.quote}"
                </blockquote>
                <div className="mt-6 pt-6 border-t border-border/60 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-sm bg-primary text-primary-foreground font-semibold flex items-center justify-center">
                    {tst.name.slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-medium">{tst.name}</div>
                    <div className="text-xs text-muted-foreground">{tst.role} · {tst.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================  FAQ  ========================= */}
      <section id="faq" data-testid="faq-section" className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{lang === "ar" ? "الأسئلة الشائعة" : "FAQ"}</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight text-primary">{t.faq.title}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t.faq.subtitle}</p>
          </div>
          <div className="border-y border-border/60 divide-y divide-border/60">
            {t.faq.items.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between py-5 md:py-6 text-start hover:opacity-80 transition"
                  >
                    <span className="font-display text-lg md:text-xl text-primary pe-4">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180 text-[#B8875D]" : ""}`} />
                  </button>
                  <div className={`grid overflow-hidden transition-all duration-300 ${isOpen ? "grid-rows-[1fr] pb-5 md:pb-6" : "grid-rows-[0fr]"}`}>
                    <div className="min-h-0">
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-3xl">{item.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================  LIVE DEMO  ========================= */}
      <section id="try-it" data-testid="demo-section" className="py-24 md:py-32 relative overflow-hidden">
        <div className="hero-blob" style={{ background: "#B8875D", width: 480, height: 480, top: -80, insetInlineEnd: -100, opacity: 0.15 }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{t.demo.kicker}</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight text-primary">{t.demo.title}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t.demo.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Form */}
            <div className="nx-card p-6 md:p-8 lg:col-span-2" data-testid="demo-form-card">
              <label className="block mb-4">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.demo.businessLabel}</span>
                <input data-testid="demo-business-input" value={demoForm.business_name}
                  onChange={(e) => setDemoForm({ ...demoForm, business_name: e.target.value })}
                  placeholder={t.demo.businessPh}
                  className="mt-2 w-full bg-background border border-border rounded-sm px-3 py-2.5 outline-none focus:border-primary" />
              </label>
              <label className="block mb-4">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.demo.industryLabel}</span>
                <select data-testid="demo-industry-select" value={demoForm.industry}
                  onChange={(e) => setDemoForm({ ...demoForm, industry: e.target.value })}
                  className="mt-2 w-full bg-background border border-border rounded-sm px-3 py-2.5 outline-none focus:border-primary">
                  {t.demo.industries.map((i) => <option key={i}>{i}</option>)}
                </select>
              </label>
              <div className="mb-4">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.demo.goalsLabel}</span>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {t.demo.goals.map((g) => (
                    <button key={g} type="button" data-testid={`demo-goal-${g}`}
                      onClick={() => setDemoForm({ ...demoForm, goals: toggleIn(demoForm.goals, g) })}
                      className={`text-xs px-2.5 py-1 rounded-sm border transition ${demoForm.goals.includes(g) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-6">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.demo.channelsLabel}</span>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {t.demo.channels.map((c) => (
                    <button key={c} type="button" data-testid={`demo-channel-${c}`}
                      onClick={() => setDemoForm({ ...demoForm, social_channels: toggleIn(demoForm.social_channels, c) })}
                      className={`text-xs px-2.5 py-1 rounded-sm border transition ${demoForm.social_channels.includes(c) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <button data-testid="demo-generate-btn" onClick={runDemo}
                disabled={demoBusy || !demoForm.business_name}
                className="nx-btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {demoBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.demo.loading}</> : <><Wand2 className="w-4 h-4" /> {t.demo.generateBtn}</>}
              </button>
              {demoError && <div data-testid="demo-error" className="mt-3 text-xs text-destructive">{demoError}</div>}
            </div>

            {/* Result */}
            <div className="nx-card p-6 md:p-8 lg:col-span-3 relative" data-testid="demo-result-card">
              {!demoResult && !demoBusy && (
                <div className="min-h-[300px] flex flex-col items-center justify-center text-center text-muted-foreground">
                  <div className="w-14 h-14 rounded-sm bg-primary/5 flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-[#B8875D]" />
                  </div>
                  <div className="text-sm max-w-xs">{lang === "ar" ? "املأ النموذج على اليمين، وسنبني خطّة نموّ مخصّصة بالكامل — على الفور." : "Fill the form and we'll build a fully personalized growth plan — instantly."}</div>
                </div>
              )}
              {demoBusy && (
                <div className="min-h-[300px] flex flex-col items-center justify-center text-center">
                  <Loader2 className="w-8 h-8 text-[#B8875D] animate-spin mb-4" />
                  <div className="text-sm text-muted-foreground">{t.demo.loading}</div>
                </div>
              )}
              {demoResult && (
                <div className="animate-fade-in-up">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#B8875D]">{t.demo.resultTitle}</div>
                      <div className="mt-1 text-lg font-display">{demoForm.business_name}</div>
                    </div>
                    <div className="text-end">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.demo.scoreLabel}</div>
                      <div className="font-display text-4xl text-primary tabular-nums">{demoResult.plan.growth_score}</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-sm border border-[#B8875D]/30 bg-[#B8875D]/5 mb-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#B8875D]">{t.demo.milestoneLabel}</div>
                    <div className="text-sm mt-1 leading-relaxed">{demoResult.plan.next_milestone}</div>
                  </div>
                  <div className="mb-5">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t.demo.prioritiesLabel}</div>
                    <ul className="space-y-2">
                      {(demoResult.plan.weekly_priorities || []).slice(0, 4).map((p, i) => (
                        <li key={i} className="flex items-start gap-3 py-1 border-b border-border/40 last:border-0">
                          <span className="font-display text-lg text-muted-foreground/60 w-6">0{i + 1}</span>
                          <div className="flex-1">
                            <div className="text-sm font-medium">{p.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>
                          </div>
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-border rounded-sm shrink-0">{p.impact}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-border/60">
                    <div className="text-xs text-muted-foreground">
                      {t.demo.remainingLabel}: <span className="font-medium text-primary">{demoResult.remaining}</span>
                    </div>
                    <Link to="/register" data-testid="demo-signup-cta" className="nx-btn-accent inline-flex items-center gap-2 text-sm">
                      {t.demo.signupCta} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* =========================  FINAL CTA  ========================= */}
      <section id="contact" data-testid="final-cta-section" className="py-24 md:py-32 relative overflow-hidden">
        <div className="hero-blob" style={{ background: "#0A1F44", width: 600, height: 600, top: -200, insetInlineStart: "50%", transform: "translateX(-50%)", opacity: 0.08 }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-medium leading-tight text-primary">{t.finalCta.title}</h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground">{t.finalCta.subtitle}</p>
          <Link to="/register" data-testid="final-cta-btn" className="nx-btn-accent inline-flex items-center gap-2 mt-10 text-base px-8 py-4 group">
            {t.finalCta.cta}
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <div className="mt-6 text-xs text-muted-foreground">{t.finalCta.caption}</div>
        </div>
      </section>

      {/* =========================  FOOTER  ========================= */}
      <footer data-testid="footer" className="bg-primary text-primary-foreground pt-16 md:pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10">
            <div className="col-span-2">
              <Link to="/" data-testid="landing-footer-brand" className="inline-flex items-center gap-2 font-display font-semibold text-2xl">
                <img
                  src="/brand/daam-mark-gold-128.png?v=2"
                  alt="DAAM"
                  width={30}
                  height={30}
                  style={{ width: 30, height: 30, objectFit: "contain" }}
                  data-testid="landing-footer-logo-mark"
                />
                <span
                  style={{
                    background: "linear-gradient(135deg,#F7E29F 0%,#EACB6E 25%,#C79A45 55%,#9E7326 85%,#6E4B16 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  DAAM
                </span>
              </Link>
              <p className="mt-4 text-sm text-white/60 max-w-xs leading-relaxed">{t.footer.tagline}</p>
              <div className="mt-6 flex items-center gap-3">
                {[Twitter, Instagram, Linkedin, Youtube].map((I, i) => (
                  <a key={i} href="#" data-testid={`footer-social-${i}`} className="w-9 h-9 rounded-sm border border-white/20 flex items-center justify-center hover:bg-[#B8875D] hover:border-[#B8875D] transition-colors">
                    <I className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{t.footer.product}</div>
              <ul className="space-y-2.5 text-sm text-white/70">
                {t.footer.productLinks.map((l, i) => <li key={i}><a href="#features" className="hover:text-white transition">{l}</a></li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{t.footer.company}</div>
              <ul className="space-y-2.5 text-sm text-white/70">
                {t.footer.companyLinks.map((l, i) => <li key={i}><a href="#" className="hover:text-white transition">{l}</a></li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{t.footer.legal}</div>
              <ul className="space-y-2.5 text-sm text-white/70">
                {t.footer.legalLinks.map((l, i) => {
                  const to = i === 0 ? "/terms" : i === 1 ? "/privacy" : "#";
                  return (
                    <li key={i}>
                      <Link to={to} data-testid={`landing-footer-legal-${i}`} className="hover:text-white transition">{l}</Link>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{t.footer.contact}</div>
              <ul className="space-y-2.5 text-sm text-white/70">
                <li>
                  <a
                    href={`mailto:${t.footer.email}`}
                    data-testid="footer-email-link"
                    className="hover:text-white transition break-all"
                  >
                    {t.footer.email}
                  </a>
                </li>
                <li className="tabular-nums" dir="ltr">
                  <a
                    href={`tel:${t.footer.phoneHref}`}
                    data-testid="footer-phone-link"
                    className="hover:text-white transition"
                  >
                    {t.footer.phone}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
            <div>© {new Date().getFullYear()} DAAM. {t.footer.rights}</div>
            <div className="flex items-center gap-2">
              <span>{t.footer.madeIn}</span>
              <span className="text-[#B8875D]">♥</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
